import { ref } from 'vue'
import DataTableItemMobile from '../components/DataTableItemMobile.vue'
import { type PDataTableRowIdValue } from '../types'

export type ScrollableItem = InstanceType<typeof DataTableItemMobile>

type ScrollSync = {
  onScroll: (xPos: number) => void
  registerItem: (item: ScrollableItem) => void
  reset: () => void
}

const createScrollSync = (): ScrollSync => {
  const items = ref<ScrollableItem[]>([])

  let ticking = false
  let lastXPos = 0

  const syncScroll = (xPos: number): void => {
    items.value.forEach((item) => {
      item.setScrollHorizontalPosition(xPos)
    })
  }

  const throttledSyncScroll = () => {
    syncScroll(lastXPos)
    ticking = false
  }

  const registerItem = (item: ScrollableItem) => {
    items.value.push(item)
  }

  const onScroll = (xPos: number): void => {
    lastXPos = xPos

    if (!ticking) {
      window.requestAnimationFrame(throttledSyncScroll)
      ticking = true
    }
  }

  const reset = (): void => {
    syncScroll(0)
  }

  return {
    onScroll,
    registerItem,
    reset,
  }
}

export type MobileDataTableScrollSync = {
  parent: ScrollSync
  child: {
    registerItem: (parentRowId: PDataTableRowIdValue, item: ScrollableItem | null) => void
    onScroll: (parentRowId: PDataTableRowIdValue, xPos: number) => void
  }
  reset: () => void
}

export const useMobileDataTableScrollSync = (): MobileDataTableScrollSync => {
  const parent = createScrollSync()
  const childGroups = new Map<PDataTableRowIdValue, ScrollSync>()

  const getChildGroup = (parentRowId: PDataTableRowIdValue): ScrollSync => {
    let group = childGroups.get(parentRowId)
    if (!group) {
      group = createScrollSync()
      childGroups.set(parentRowId, group)
    }
    return group
  }

  const registerItem = (parentRowId: PDataTableRowIdValue, item: ScrollableItem | null): void => {
    if (item) {
      getChildGroup(parentRowId).registerItem(item)
    }
  }

  const onScroll = (parentRowId: PDataTableRowIdValue, xPos: number): void => {
    getChildGroup(parentRowId).onScroll(xPos)
  }

  const reset = (): void => {
    parent.reset()
    childGroups.forEach((group) => group.reset())
  }

  return {
    parent,
    child: {
      registerItem,
      onScroll,
    },
    reset,
  }
}
